/**
 * API Configuration
 * Determines the base URL for API requests based on environment
 *
 * In production with Nginx:
 * - Frontend is served from /saa-dashboard
 * - API requests to /api are proxied to backend (e.g. localhost:8083)
 * - Use empty string (relative URLs) so requests go to /api on same origin
 *
 * In development:
 * - Frontend runs on localhost:3000/3010 (npm start)
 * - setupProxy.js forwards /api to backend (default http://localhost:8083)
 * - Use empty string so requests hit same origin and get proxied to backend
 * - If backend runs on another port, set REACT_APP_API_URL=http://localhost:PORT
 */
export const getApiBase = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

  if (isLocalhost) {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl) return apiUrl;
    return '';
  }

  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) return apiUrl;

  // Production: relative URLs; Nginx proxies /api to backend
  return '';
};

/** Backend origin for WebSocket and for report URLs. In dev, use backend port so report opens from backend, not SPA. */
export const getBackendOrigin = (): string => {
  if (typeof window === 'undefined') return '';
  const base = getApiBase();
  if (base) return base;
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) return apiUrl;
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:8083';
  return `${window.location.protocol}//${window.location.host}`;
};

/** Full URL for an API path (e.g. /api/slo/status). Uses backend origin so requests always reach the API (avoids 404 when proxy is not used). */
export const getApiUrl = (path: string): string => {
  const base = getApiBase() || getBackendOrigin();
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base.replace(/\/$/, '')}${normalized}` : normalized;
};

/** Base URL for report links (open/download). Always absolute so new tab hits backend, not SPA router. */
export const getReportBaseUrl = (): string => getBackendOrigin();

